import {
  MIRA_PERIOD_ROLLING_12,
  MIRA_SECTIONS,
  MiraAvailabilityState,
  MiraSection,
  MiraSectionDocument,
} from "@/data/models/Mira";
import { MiraViewContext, metricTitle } from "@/utils/MiraDataQualityView";
import { MIRA_SECTION_ICONS } from "@/utils/MiraOverview";
import { isShownMetric } from "@/utils/MiraDataQuality";

/**
 * The logic behind the "Mehr Kennzahlen freischalten" page: which figures wait on the company's
 * data over the last 12 months, grouped by the one thing they need recorded, and how close each is.
 *
 * Kept out of the component so it can be tested without mounting anything. Translation and formatting
 * come in through {@link MiraViewContext}; this module never imports Vuetify or vue-i18n. See
 * `docs/analytics/mira-office-screens.md`.
 */

/** The icon for a section with none of its own, matching the per-screen unlock list. */
const SECTION_ICON_FALLBACK = "mdi-chart-box-outline";

/** One figure an instruction would unlock: its title, its section, and that section's icon. */
export interface MiraUnlockFigure {
  metricId: string;
  title: string;
  section: MiraSection;
  sectionIcon: string;
}

/** The progress toward an entry's goal, present only where IRES Cloud published a target. */
export interface MiraUnlockProgress {
  /** How full the bar is: have / needed, capped at 1 (a value past its goal never overfills). */
  fraction: number;
  /** The line under the bar, e.g. "39 % der Projekte erfasst · ab 40 % sichtbar". */
  text: string;
}

/**
 * What unlocks an entry: something the company can record (`RECORD`, a share of its population), or
 * simply more cases (`CASES`, a figure that asks for nothing and only waits for enough projects). The
 * CASES part gathers both a subpopulation `COUNT` target (its own "too few X" text) and the generic
 * `CASES` target (too few projects overall, one shared instruction). The page shows the two kinds apart,
 * so a heading is always a task where it is shown as one.
 */
export type MiraUnlockKind = "RECORD" | "CASES";

/** One card on the page: an instruction, the figures it unlocks, and the progress toward it. */
export interface MiraUnlockEntry {
  kind: MiraUnlockKind;
  /** The recorded action, e.g. "Ordne die aufgestellten Geräte dem Projekt zu." — also the group key. */
  instruction: string;
  figures: MiraUnlockFigure[];
  progress: MiraUnlockProgress | null;
}

/** The whole page: the entries to show and the count the header announces. */
export interface MiraUnlockView {
  entries: MiraUnlockEntry[];
  /** How many figures wait on data across every entry (the header count). */
  waitingCount: number;
}

/** What one instruction group accumulates while the sections are scanned. */
interface UnlockGroup {
  instruction: string;
  figures: MiraUnlockFigure[];
  /** For a SHARE target: the highest coverage among the group's figures, i.e. the nearest to its goal. */
  maxCoverage: number;
  shareNeeded: number | null;
  count: { have: number; needed: number } | null;
}

/** The instruction for a metric: Office's own wording, or the generic line where it has no key yet. */
function unlockInstruction(metricId: string, ctx: MiraViewContext): string {
  const key = `mira.metrics.${metricId}.unlock`;
  return ctx.t(ctx.te(key) ? key : "mira.dataQuality.unlocks.fallback");
}

/**
 * A share of 0 to 1 as a whole percent, floored so it never reaches the goal before the figure does
 * (39.7 % reads "39 %", not "40 %"). The small epsilon absorbs float error so 0.29 does not floor to
 * 28, while a value genuinely below its whole percent still rounds down.
 */
function flooredPercent(share: number, ctx: MiraViewContext): string {
  return ctx.t("mira.format.percent", [ctx.formatNumber(Math.floor(share * 100 + 1e-9))]);
}

/** The progress line and bar fraction for a group, or `null` where no figure in it carries a target. */
function buildProgress(group: UnlockGroup, ctx: MiraViewContext): MiraUnlockProgress | null {
  if (group.shareNeeded !== null) {
    const have = group.maxCoverage;
    const needed = group.shareNeeded;
    return {
      fraction: needed > 0 ? Math.min(1, have / needed) : 0,
      text: ctx.t("mira.unlock.shareProgress", [flooredPercent(have, ctx), flooredPercent(needed, ctx)]),
    };
  }

  if (group.count !== null) {
    const { have, needed } = group.count;
    return {
      fraction: needed > 0 ? Math.min(1, have / needed) : 0,
      text: ctx.t("mira.unlock.countProgress", [ctx.formatNumber(have), ctx.formatNumber(needed)]),
    };
  }

  return null;
}

/**
 * The page's entries and waiting count, from the section documents at the rolling year.
 *
 * **Last 12 months only** (no period picker): the page is where the figures hidden on the rolling-year
 * screens belong. Every `INSUFFICIENT_DATA` figure is taken, grouped by its resolved instruction text
 * (two figures that need the same thing recorded become one entry listing both), and sorted nearest to
 * its goal first (the goal-gradient: a company sees what is almost in reach), then the entries with no
 * published target, then by instruction. The displayed share is floored so a still-locked figure never
 * reads as at its goal.
 *
 * @param sections - the section documents that exist.
 * @param ctx - translation and number formatting.
 */
export function buildUnlockView(
  sections: Partial<Record<MiraSection, MiraSectionDocument>>,
  ctx: MiraViewContext
): MiraUnlockView {
  const byInstruction = new Map<string, UnlockGroup>();
  let waitingCount = 0;

  for (const section of MIRA_SECTIONS) {
    const metrics = sections[section]?.periods[MIRA_PERIOD_ROLLING_12]?.metrics ?? {};
    for (const metricId of Object.keys(metrics).sort()) {
      const metric = metrics[metricId];
      // A retired or hidden figure has no screen, so it is never offered to unlock (`isShownMetric`).
      if (metric.state !== MiraAvailabilityState.INSUFFICIENT_DATA || !isShownMetric(metricId)) {
        continue;
      }
      waitingCount += 1;

      const target = metric.unlockTarget;
      // A CASES target is "too few projects overall": there is nothing to record, so it is grouped under
      // one generic instruction, never the figure's own "record field X" text (which would be wrong here).
      const instruction =
        target?.kind === "CASES"
          ? ctx.t("mira.unlock.casesInstruction", [ctx.formatNumber(target.needed)])
          : unlockInstruction(metricId, ctx);
      let group = byInstruction.get(instruction);
      if (!group) {
        group = { instruction, figures: [], maxCoverage: 0, shareNeeded: null, count: null };
        byInstruction.set(instruction, group);
      }

      group.figures.push({
        metricId,
        title: metricTitle(metricId, ctx),
        section,
        sectionIcon: MIRA_SECTION_ICONS[section] ?? SECTION_ICON_FALLBACK,
      });
      group.maxCoverage = Math.max(group.maxCoverage, metric.coverage ?? 0);

      if (target?.kind === "SHARE" && group.shareNeeded === null) {
        group.shareNeeded = target.needed;
      } else if (target?.kind === "COUNT" || target?.kind === "CASES") {
        // Both carry a case count, so both drive the "{0} von {1} nötigen Fällen" progress line. Several
        // figures can share one instruction (every CASES figure does), so the line shows the one nearest
        // its goal, as a share group shows its highest coverage.
        if (group.count === null || target.have > group.count.have) {
          group.count = { have: target.have, needed: target.needed };
        }
      }
    }
  }

  const entries: MiraUnlockEntry[] = [...byInstruction.values()].map((group) => ({
    // A count target and no share target: nothing to record, the figure waits for more cases.
    kind: group.count !== null && group.shareNeeded === null ? "CASES" : "RECORD",
    instruction: group.instruction,
    figures: group.figures,
    progress: buildProgress(group, ctx),
  }));

  // Nearest to its goal first; entries without a target (fraction treated as -1) after all targeted
  // ones; ties by instruction, so the order is stable across loads.
  entries.sort((a, b) => {
    const fractionA = a.progress ? a.progress.fraction : -1;
    const fractionB = b.progress ? b.progress.fraction : -1;
    return fractionB - fractionA || a.instruction.localeCompare(b.instruction);
  });

  return { entries, waitingCount };
}
